import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** @type {object | undefined} */
  #pendingUpdate;

  /**
   * Handles a variant update event by replacing the current media gallery with a new one. The swap waits (up to
   * 3 s) until the new gallery's lead photo is downloaded, so it never shows an empty frame; a newer update wins.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const token = (this.#pendingUpdate = {});
    leadPhotoReady(newMediaGallery).then(() => {
      if (this.#pendingUpdate === token && this.isConnected) this.replaceWith(newMediaGallery);
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Resolves once the gallery's first image is downloaded and decoded, or after 3 s.
 * @param {Element} gallery - A gallery that isn't in the page yet.
 * @returns {Promise<void>}
 */
function leadPhotoReady(gallery) {
  const lead = gallery.querySelector('slideshow-slide img');
  if (!(lead instanceof HTMLImageElement)) return Promise.resolve();

  // Attributes, not properties: the parsed response doesn't resolve protocol-relative URLs against this page.
  const image = new Image();
  image.sizes = lead.getAttribute('sizes') || '';
  image.srcset = lead.getAttribute('srcset') || '';
  image.src = lead.getAttribute('src') || '';
  if (image.complete && image.naturalWidth) return Promise.resolve();

  return Promise.race([image.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 3000))]);
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
